import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { Strategy } from "@repo/api";
import { useRankingRule } from "./useRankingRule";
import { useUniverseDetail } from "../api/useUniversesApi";

export const useSettings = (strategy: Strategy) => {
  const { convertStrategyToRankingRule } = useRankingRule(strategy);
  const universeQuery = useUniverseDetail(strategy.poolName);
  const [maxNumberOfStocks, setMaxNumberOfStocks] = useState<number>(30);

  const prevStrategyIdRef = useRef<string | undefined>(undefined);
  const prevBacktestSettingsRef = useRef<string>("");

  const resetStrategySettings = useCallback(() => {
    setNumberOfStocks(strategy.backtest.numberOfStocks);
    setInitialCapital(strategy.backtest.startCash);
    setBacktestingPeriod(strategy.backtest.period);
    setRankingRule(convertStrategyToRankingRule());
    setPortfolioConstruction(strategy.backtest.portfolioConstruction);
    setLeverage(strategy.backtest.leverage);
  }, [strategy, convertStrategyToRankingRule]);

  // Update settings when strategy changes OR when backtest settings change (e.g., after WebSocket REFRESH)
  useEffect(() => {
    // Create a stable hash of backtest settings to detect changes
    const currentBacktestSettings = JSON.stringify({
      period: strategy.backtest.period,
      numberOfStocks: strategy.backtest.numberOfStocks,
      startCash: strategy.backtest.startCash,
      portfolioConstruction: strategy.backtest.portfolioConstruction,
      leverage: strategy.backtest.leverage,
      ascending: strategy.backtest.ascending,
    });

    const isNewStrategy = strategy.modelId !== prevStrategyIdRef.current;
    const hasBacktestSettingsChanged = currentBacktestSettings !== prevBacktestSettingsRef.current;

    // Reset settings if it's a new strategy OR if backtest settings changed (e.g., after recalculation + page reload)
    if (isNewStrategy || hasBacktestSettingsChanged) {
      resetStrategySettings();
      prevStrategyIdRef.current = strategy.modelId;
      prevBacktestSettingsRef.current = currentBacktestSettings;
    }
  }, [strategy.modelId, strategy.backtest, resetStrategySettings]);

  const poolName = useMemo(
    () => universeQuery.data?.universe,
    [universeQuery.data]
  );

  const [numberOfStocks, setNumberOfStocks] = useState(
    strategy.backtest.numberOfStocks
  );
  const [initialCapital, setInitialCapital] = useState(
    strategy.backtest.startCash
  );
  const [backtestingPeriod, setBacktestingPeriod] = useState(
    strategy.backtest.period
  );

  const [portfolioConstruction, setPortfolioConstruction] = useState(
    strategy.backtest.portfolioConstruction
  );
  const [leverage, setLeverage] = useState(strategy.backtest.leverage);
  const [rankingRule, setRankingRule] = useState(
    convertStrategyToRankingRule()
  );

  const initialStrategySettings = useMemo(
    () => ({
      numberOfStocks: strategy.backtest.numberOfStocks,
      initialCapital: strategy.backtest.startCash,
      backtestingPeriod: strategy.backtest.period,
      portfolioConstruction: strategy.backtest.portfolioConstruction,
      leverage: strategy.backtest.leverage,
      rankingRule: convertStrategyToRankingRule(),
    }),
    [strategy, convertStrategyToRankingRule]
  );

  useEffect(() => {
    if (universeQuery.data) {
      const maxStocksByConstruction =
        portfolioConstruction === "LongShort"
          ? Math.floor(universeQuery.data.assetsCount / 2)
          : universeQuery.data.assetsCount;
      const calculatedMax = Math.min(30, maxStocksByConstruction);
      setMaxNumberOfStocks(calculatedMax);
      if (numberOfStocks > calculatedMax) {
        setNumberOfStocks(calculatedMax);
      }
    }
  }, [universeQuery.data, portfolioConstruction, numberOfStocks]);

  return {
    initialStrategySettings,
    numberOfStocks,
    maxNumberOfStocks,
    initialCapital,
    backtestingPeriod,
    rankingRule,
    portfolioConstruction,
    leverage,
    poolName,
    setNumberOfStocks,
    setInitialCapital,
    setBacktestingPeriod,
    setRankingRule,
    setPortfolioConstruction,
    setLeverage,
    resetStrategySettings,
  };
};
